import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  StyledAutocomplete,
  StyledSelect,
  StyledTableCellHistory,
  StyledTableRowHistory,
  StyledTextField,
  theme,
} from "../StyledComponents";
import { ThemeProvider } from "@mui/material/styles";
import axios from "axios";

const unitOptions = ["mg/dL", "g/dL", "mmol/L", "cells/mcL", "µg/L", "L", "%"];

function getApiAuthHeaders() {
  const token =
    sessionStorage.getItem("accessToken") !== undefined &&
    sessionStorage.getItem("accessToken") !== null
      ? sessionStorage.getItem("accessToken")
      : "";
  return { Authorization: `Bearer ${token}` };
}

/** Title for accordion from database panel or LOINC resolve wrapper */
function getResolvedPanelTitle(resolved, fallback) {
  if (!resolved || typeof resolved !== "object" || Array.isArray(resolved)) {
    return fallback;
  }
  if (resolved.panel_name || resolved.term) {
    return resolved.panel_name || resolved.term;
  }
  const roots = resolved.data ?? resolved.Data;
  if (Array.isArray(roots) && roots.length && roots[0]) {
    const root = roots[0];
    return (
      root.LONG_COMMON_NAME ||
      root.DisplayName ||
      root.displayName ||
      root.COMPONENT ||
      root.LOINC_NUMBER ||
      fallback
    );
  }
  return fallback;
}

const LoincAccordionPanelInput = ({ data, setData }) => {
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPanelOptionId, setSelectedPanelOptionId] = useState(null);
  const [expandedPanelId, setExpandedPanelId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const latestSearchTermRef = useRef("");
  const panelEntries = Array.isArray(data) ? data : [];

  const extractSearchPanelList = (body) => {
    if (!body) return [];
    if (Array.isArray(body)) return body;
    if (Array.isArray(body.data)) return body.data;
    if (Array.isArray(body.Data)) return body.Data;
    if (Array.isArray(body.results)) return body.results;
    return [];
  };
  // 🔹 recursive flatten helper
  // const flattenTests = (items, panelName, seen = new Set()) => {
  //   let flat = [];
  //   for (const t of items) {
  //     if (!t.LOINC_NUMBER || seen.has(t.LOINC_NUMBER)) continue;
  //     seen.add(t.LOINC_NUMBER);

  //     flat.push({
  //       id: `${t.LOINC_NUMBER}_${panelName}`,
  //       test: t.Name || "-",
  //       code: t.LOINC_NUMBER || "-",
  //       // description: t.SHORTNAME || t.LONG_COMMON_NAME || "-",
  //       value: "",
  //       unit: t.Example_UCUM_UNITS || "",
  //     });

  //     if (Array.isArray(t.Children)) {
  //       flat = flat.concat(flattenTests(t.Children, panelName, seen));
  //     }
  //     if (Array.isArray(t.grandChildren)) {
  //       flat = flat.concat(flattenTests(t.grandChildren, panelName, seen));
  //     }
  //   }
  //   return flat;
  // };

  // skip top-level test, only include children & grandchildren
  const flattenTests = (items, panelName, seen = new Set()) => {
    let flat = [];
    for (const t of items) {
      // ⛔ skip the parent panel row (just recurse its children)
      if (!t.Children && !t.grandChildren) {
        if (!t.LOINC_NUMBER || seen.has(t.LOINC_NUMBER)) continue;
        seen.add(t.LOINC_NUMBER);

        flat.push({
          id: `${t.LOINC_NUMBER}_${panelName}`,
          test: t.displayName || t.DisplayName || t.Name || "-",
          code: t.LOINC_NUMBER || "-",
          // description: t.SHORTNAME || t.LONG_COMMON_NAME || "-",
          value: "",
          unit: t.Example_UCUM_UNITS || "",
        });
      }

      if (Array.isArray(t.Children)) {
        flat = flat.concat(flattenTests(t.Children, panelName, seen));
      }
      if (Array.isArray(t.grandChildren)) {
        flat = flat.concat(flattenTests(t.grandChildren, panelName, seen));
      }
    }
    return flat;
  };

  /** Resolved panel API: { tests: [{ test_name, loinc_code, unit_primary, ... }] } */
  const formatResolvedPanelTests = (resolved, panelName) => {
    const tests = resolved?.tests;
    if (!Array.isArray(tests)) return [];
    return tests.map((t, index) => {
      const code = t.loinc_code || t.LOINC_NUMBER || "-";
      const idBase = t.test_id || code || `idx-${index}`;
      const id = `${idBase}_${panelName}_${index}`;
      return {
        id,
        test_id: t.test_id,
        test: t.test_name || t.name || "-",
        code,
        value: "",
        unit: t.unit_primary || t.unit || "",
        group_name: t.group_name || "",
        critical_notes: t.critical_notes || "",
      };
    });
  };

  const buildTestsFromResolveResponse = (resolved, panelName) => {
    if (
      resolved &&
      typeof resolved === "object" &&
      !Array.isArray(resolved) &&
      Array.isArray(resolved.tests)
    ) {
      return formatResolvedPanelTests(resolved, panelName);
    }

    // LOINC resolve: { source: "loinc", loincNumber, data: [ panelRoot, ... ] }
    const loincRoots = resolved?.data ?? resolved?.Data;
    if (
      resolved &&
      typeof resolved === "object" &&
      !Array.isArray(resolved) &&
      Array.isArray(loincRoots) &&
      (resolved.source === "loinc" || resolved.loincNumber != null)
    ) {
      return flattenTests(loincRoots, panelName);
    }

    const items = Array.isArray(resolved)
      ? resolved
      : resolved
        ? [resolved]
        : [];
    return flattenTests(items, panelName);
  };

  // 🔹 fetch panels dynamically from search_test_panels
  // const handlePanelSearch = async (value) => {
  //   if (!value || value.length < 2) {
  //     setSearchResults([]);
  //     return;
  //   }
  //   try {
  //     const res = await axios.get(
  //       `http://3.6.121.41:8081/loincserv/v1/search`,
  //       {
  //         params: {
  //           class: "ALL",
  //           classType: "ALL",
  //           component: "ALL",
  //           exampleUnits: "ALL",
  //           limit: 20,
  //           method: "ALL",
  //           panelType: "PANEL",
  //           property: "ALL",
  //           scale: "ALL",
  //           sortByRank: false,
  //           status: "ACTIVE",
  //           system: "ALL",
  //           term: value,
  //           timing: "ALL",
  //         },
  //       }
  //     );

  //     const results = res.data.map((item) => ({
  //       panel_name:
  //         item.DISPLAY_NAME ||
  //         item.LONG_COMMON_NAME ||
  //         item.COMPONENT ||
  //         item.LOINC_NUMBER,
  //       loinc_num: item.LOINC_NUMBER,
  //     }));
  //     setSearchResults(results);
  //   } catch (err) {
  //     console.error("Error fetching panels:", err);
  //   }
  // };

  const handlePanelSearch = async (value) => {
    const term = typeof value === "string" ? value.trim() : "";
    latestSearchTermRef.current = term;

    if (!term || term.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_ROUTE_URL}search_test_panels/`,
        { params: { name: term }, headers: getApiAuthHeaders() }
      );

      if (latestSearchTermRef.current !== term) return;

      const raw = extractSearchPanelList(res.data);
      const results = raw.map((item, index) => ({
        optionId:
          item.id ??
          `${item.source ?? "unknown"}-${item.loincNumber ?? ""}-${item.panel_id ?? ""}-${index}`,
        panel_name: item.term || item.name || "-",
        source: item.source,
        panel_id: item.panel_id,
        loincNumber: item.loincNumber,
      }));

      setSearchResults(results);
    } catch (err) {
      if (latestSearchTermRef.current === term) {
        setSearchResults([]);
      }
      console.error("Error fetching panels:", err);
    }
  };

  // 🔹 debounce search (wait 1s after typing stops)
  useEffect(() => {
    const t = (searchText ?? "").trim();
    if (t.length < 2) {
      latestSearchTermRef.current = t;
      setSearchResults([]);
      return;
    }
    const timeout = setTimeout(() => {
      handlePanelSearch(searchText);
    }, 1000);
    return () => clearTimeout(timeout);
  }, [searchText]);

  // 🔹 fetch tests of a selected panel using lookup
  // const handlePanelSelect = async (panel) => {
  //   if (!panel) return;
  //   setSelectedPanel(panel.panel_name);

  //   if (panelEntries.some((p) => p.panel_name === panel.panel_name)) return;

  //   try {
  //     const res = await axios.get(
  //       `http://3.6.121.41:8081/loincserv/v1/lookup`,
  //       { params: { loincNumber: panel.loinc_num } }
  //     );

  //     const formattedTests = flattenTests(res.data, panel.panel_name);

  //     const updated = [
  //       ...panelEntries,
  //       {
  //         panel_name: panel.panel_name,
  //         tests: formattedTests,
  //       },
  //     ];

  //     setData(updated);
  //     setExpandedPanel(panel.panel_name);
  //   } catch (err) {
  //     console.error("Error fetching panel tests:", err);
  //   }
  // };

  const handlePanelSelect = async (panel) => {
    if (!panel) {
      setSelectedPanelOptionId(null);
      return;
    }
    setSelectedPanelOptionId(panel.optionId);

    if (panelEntries.some((p) => p.panelOptionId === panel.optionId)) return;

    let body;
    if (panel.source === "database") {
      if (!panel.panel_id) {
        console.error("database panel missing panel_id", panel);
        return;
      }
      body = { source: "database", panel_id: panel.panel_id };
    } else {
      body = { source: "loinc", loincNumber: panel.loincNumber };
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_ROUTE_URL}resolve_test_panel/`,
        body,
        { headers: getApiAuthHeaders() }
      );

      const resolved = res.data?.data ?? res.data;
      const displayName = getResolvedPanelTitle(resolved, panel.panel_name);

      const formattedTests = buildTestsFromResolveResponse(
        resolved,
        displayName
      );

      const updated = [
        ...panelEntries,
        {
          panelOptionId: panel.optionId,
          panel_name: displayName,
          tests: formattedTests,
        },
      ];

      setData(updated);
      setExpandedPanelId(panel.optionId);
    } catch (err) {
      console.error("Error fetching panel tests:", err);
    }
  };

  const handleTestChange = (panelIndex, testId, field, newValue) => {
    const updatedPanels = [...panelEntries];
    updatedPanels[panelIndex].tests = updatedPanels[panelIndex].tests.map(
      (row) => (row.id === testId ? { ...row, [field]: newValue } : row)
    );
    setData(updatedPanels);
  };

  const handleDeleteTest = (panelIndex, testId) => {
    const updatedPanels = [...panelEntries];
    updatedPanels[panelIndex].tests = updatedPanels[panelIndex].tests.filter(
      (row) => row.id !== testId
    );
    setData(updatedPanels);
  };

  const handleDeletePanel = (panelOptionId) => {
    const updated = panelEntries.filter(
      (p) => p.panelOptionId !== panelOptionId
    );
    setData(updated);
    if (expandedPanelId === panelOptionId) setExpandedPanelId(null);
  };

  return (
    <ThemeProvider theme={theme}>
      <Paper sx={{ padding: 3, marginTop: 3 }}>
        <Typography variant="h6" gutterBottom>
          Initial Observation
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <StyledAutocomplete
              fullWidth
              options={searchResults}
              filterOptions={(options) => options}
              getOptionLabel={(opt) => opt?.panel_name || ""}
              isOptionEqualToValue={(a, b) => a?.optionId === b?.optionId}
              value={
                searchResults.find(
                  (p) => p.optionId === selectedPanelOptionId
                ) || null
              }
              onInputChange={(e, value, reason) => {
                if (reason === "reset" && value) return;
                setSearchText(value ?? "");
              }}
              onChange={(e, newVal) => {
                setSelectedPanelOptionId(newVal?.optionId ?? null);
                handlePanelSelect(newVal);
              }}
              ListboxProps={{ style: { maxHeight: 280 } }}
              renderInput={(params) => (
                <StyledTextField {...params} label="Search Panel" />
              )}
            />
          </Grid>
        </Grid>

        {/* Panels with tests */}
        {panelEntries.map((panel, panelIndex) => (
          <Accordion
            key={panel.panelOptionId || panel.panel_name}
            expanded={expandedPanelId === panel.panelOptionId}
            onChange={() =>
              setExpandedPanelId((prev) =>
                prev === panel.panelOptionId ? null : panel.panelOptionId
              )
            }
            sx={{ mt: 2, border: "1px solid #21cdc0", borderRadius: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="subtitle1">{panel.panel_name}</Typography>
                <Tooltip title="Delete Panel">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePanel(panel.panelOptionId);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer
                component={Paper}
                sx={{ border: "1px solid #ccc" }}
              >
                <Table>
                  <TableHead>
                    <StyledTableRowHistory>
                      <StyledTableCellHistory>Test Name</StyledTableCellHistory>
                      <StyledTableCellHistory>
                        LOINC Code
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>Group</StyledTableCellHistory>
                      <StyledTableCellHistory>Value</StyledTableCellHistory>
                      <StyledTableCellHistory>Unit</StyledTableCellHistory>
                      <StyledTableCellHistory>Actions</StyledTableCellHistory>
                    </StyledTableRowHistory>
                  </TableHead>
                  <TableBody>
                    {panel.tests.map((testRow) => (
                      <StyledTableRowHistory key={testRow.id}>
                        <StyledTableCellHistory>
                          {testRow.critical_notes ? (
                            <Tooltip title={testRow.critical_notes}>
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{ cursor: "help" }}
                              >
                                {testRow.test}
                              </Typography>
                            </Tooltip>
                          ) : (
                            testRow.test
                          )}
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>
                          {testRow.code}
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>
                          {testRow.group_name || "—"}
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>
                          <StyledTextField
                            value={testRow.value}
                            size="small"
                            onChange={(e) =>
                              handleTestChange(
                                panelIndex,
                                testRow.id,
                                "value",
                                e.target.value
                              )
                            }
                          />
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>
                          {/* <StyledSelect
                            size="small"
                            value={testRow.unit}
                            displayEmpty
                            onChange={(e) =>
                              handleTestChange(panelIndex, testRow.id, "unit", e.target.value)
                            }
                          >
                            <MenuItem value="">
                              <em>Select Unit</em>
                            </MenuItem>
                            {[...new Set([testRow.unit, ...unitOptions])].map(
                              (unit) => (
                                <MenuItem key={unit} value={unit}>
                                  {unit}
                                </MenuItem>
                              )
                            )}
                          </StyledSelect> */}
                          <StyledSelect
                            size="small"
                            value={testRow.unit}
                            displayEmpty
                            onChange={(e) =>
                              handleTestChange(
                                panelIndex,
                                testRow.id,
                                "unit",
                                e.target.value
                              )
                            }
                            sx={{
                              minWidth: 120, // fixed width
                              maxWidth: 150, // optional
                              height: 40, // fixed height
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                            }}
                          >
                            <MenuItem value="">
                              <em>Select Unit</em>
                            </MenuItem>
                            {[
                              ...new Set(
                                [
                                  testRow.unit,
                                  testRow.unit_secondary,
                                  ...unitOptions,
                                ].filter(Boolean)
                              ),
                            ].map((unit) => (
                              <MenuItem key={unit} value={unit}>
                                {unit}
                              </MenuItem>
                            ))}
                          </StyledSelect>
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDeleteTest(panelIndex, testRow.id)
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </StyledTableCellHistory>
                      </StyledTableRowHistory>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>
    </ThemeProvider>
  );
};

export default LoincAccordionPanelInput;
